import { format, formatDistanceToNow, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const date = parseISO(dateStr);
    return format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return dateStr;
  }
}

export function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const date = parseISO(dateStr);
    return format(date, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

export function formatDateRelative(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Hoje';
    if (isTomorrow(date)) return 'Amanhã';
    return format(date, "d 'de' MMM", { locale: ptBR });
  } catch {
    return dateStr;
  }
}

export function formatDateTime(dateStr: string | null | undefined, timeStr?: string | null): string {
  if (!dateStr) return '—';
  const datePart = formatDateShort(dateStr);
  if (!timeStr) return datePart;
  return `${datePart} às ${timeStr}`;
}

export function formatTimeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const date = parseISO(dateStr);
    return formatDistanceToNow(date, { locale: ptBR, addSuffix: true });
  } catch {
    return dateStr;
  }
}

export function isDeadlinePast(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  try {
    return isPast(parseISO(dateStr));
  } catch {
    return false;
  }
}

export function isDeadlineSoon(dateStr: string | null | undefined, days = 3): boolean {
  if (!dateStr) return false;
  try {
    const date = parseISO(dateStr);
    const future = new Date();
    future.setDate(future.getDate() + days);
    return date <= future && date >= new Date();
  } catch {
    return false;
  }
}

export const SUBJECT_LABELS: Record<string, string> = {
  recomendacao_templo: 'Recomendação do Templo',
  chamado: 'Chamado',
  ministracao: 'Ministração',
  outro: 'Outro',
};

export const MEETING_TYPE_LABELS: Record<string, string> = {
  presidencia: 'Presidência da Estaca',
  sumo_conselho: 'Sumo Conselho',
  conselho_estaca: 'Conselho da Estaca',
};

export const CALLING_TYPE_LABELS: Record<string, string> = {
  chamado: 'Chamado',
  desobrigacao: 'Desobrigação',
};

export const ACTION_LABELS: Record<string, string> = {
  created_task: 'criou a tarefa',
  completed_task: 'concluiu a tarefa',
  reopened_task: 'reabriu a tarefa',
  updated_task: 'atualizou a tarefa',
  created_interview: 'adicionou entrevista',
  scheduled_interview: 'agendou entrevista',
  completed_interview: 'concluiu entrevista',
  updated_interview: 'atualizou entrevista',
  created_calling: 'criou chamado/desobrigação',
  updated_calling: 'atualizou chamado',
  archived_calling: 'arquivou chamado',
  generated_sustain_text: 'gerou texto de apoio',
  created_meeting: 'criou reunião',
  updated_meeting: 'atualizou reunião',
  deleted_meeting: 'excluiu reunião',
  added_agenda_item: 'adicionou item à agenda',
  removed_agenda_item: 'removeu item da agenda',
  uploaded_minutes: 'enviou ata',
  generated_agenda_pdf: 'gerou PDF da agenda',
  generated_agenda_docx: 'gerou modelo de ata (.docx)',
  reverted_interview: 'reverteu entrevista para pendente',
  updated_meeting_meta: 'atualizou informações da reunião',
};

export function formatActionLabel(action: string): string {
  // Handle dynamic step labels
  if (action.startsWith('updated_calling_step_')) {
    const step = action.replace('updated_calling_step_', '');
    const stepLabels: Record<string, string> = {
      step_presidency_approval: 'marcou aprovação em Presidência',
      step_bishop_approval: 'marcou aprovação do Bispo',
      step_high_council_sustain: 'marcou apoio no Sumo Conselho',
      step_interview: 'registrou entrevista',
      step_lcr_recorded: 'marcou registro no LCR',
      step_set_apart: 'registrou designação',
    };
    if (step.startsWith('step_sustain_')) {
      const ward = step.replace('step_sustain_', '').replace(/_/g, ' ');
      return `marcou apoio em ${ward}`;
    }
    return stepLabels[step] || `atualizou step ${step}`;
  }
  return ACTION_LABELS[action] || action;
}

export function getInitials(name: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(n => n[0].toUpperCase())
    .join('');
}
